import { useTranslation } from '@/i18n';
import type { RatingIcon } from './ratingIcon';

/** 1 is the worst, 5 the best. The same scale TouchTab.Feedback stores and alerts on. */
export const RATINGS = [1, 2, 3, 4, 5] as const;
export type Rating = (typeof RATINGS)[number];


interface RatingScaleProps {
    /** Groups the five radios. Must be unique on the page — one question, one name. */
    name: string;
    /** The question, as the restaurant authored it. Becomes the group's legend. */
    question: string;
    value: Rating | null;
    onChange: (rating: Rating | null) => void;
    /** The brand's choice. See `ratingIconFrom`. */
    icon: RatingIcon;
    disabled?: boolean;
}

/**
 * One question, answered on a five-point scale drawn the way the brand asked for.
 *
 * TWO STYLES, ONE SCALE. A brand picks Stars or Emoji in the portal and every app honours it, so a guest
 * cannot get stars from the server's handheld and faces on their own phone at the same restaurant — which is
 * what happened between September and now, while this file's comment still claimed the two matched.
 *
 * The difference is only what a guest sees. Both submit 1 to 5, left to right, and reports, averages,
 * triggers and alerts cannot tell them apart.
 *
 * Drawn rather than shipped as images. The handheld uses tinted PNG drawables, which cannot take a
 * restaurant's brand ink or survive dark mode; these are `currentColor` and do both, and cost no requests.
 *
 * Native radios under the marks rather than buttons with ARIA. A radio group gives arrow-key navigation, the
 * "2 of 5" position, and the group's legend read before each option, for free and correctly. The input is
 * visually hidden but focusable, and the visible mark is the label — so a tap anywhere on it selects, and the
 * focus ring lands on something the guest can see.
 */
export function RatingScale({ name, question, value, onChange, icon, disabled }: RatingScaleProps) {
    const { t } = useTranslation();

    const stars = icon === 'Star';

    return (
        <fieldset disabled={disabled} className="min-w-0">
            <legend className="mb-2 w-full text-[0.9375rem] font-medium text-pretty">{question}</legend>

            {/*
              * Stars sit in a row and read as one bar filling up; faces are five separate things to choose
              * between, so each gets its own card. Same radios underneath either way.
              */}
            <div className={stars ? 'flex justify-center gap-1' : 'grid grid-cols-5 gap-1.5'}>
                {RATINGS.map((rating) => {
                    /*
                     * THE SELECTION RULE DIFFERS, and it is the whole of what "fill-up" means. A star at or
                     * below the chosen rating is lit, so three of five reads as three; exactly one face is
                     * ever marked, because the guest picked that face and not the two before it.
                     */
                    const lit = value != null && (stars ? rating <= value : rating === value);

                    return (
                        <label
                            key={rating}
                            className={[
                                'flex cursor-pointer items-center justify-center transition-colors',
                                stars
                                    ? 'rounded-lg p-1.5'
                                    : 'flex-col gap-1 rounded-xl border py-2.5',
                                // 1.4.1: for faces the border and background carry the selection too, so it
                                // does not rest on telling the brand colour from the muted one. A lit star
                                // differs in fill as well as colour, which is the same idea in one mark.
                                lit
                                    ? stars
                                        ? 'text-primary'
                                        : 'border-primary bg-primary/10 text-primary'
                                    : stars
                                      ? 'text-muted-foreground'
                                      : 'border-transparent text-muted-foreground',
                                // The ring follows the hidden input's focus onto the label the guest can see.
                                'has-[:focus-visible]:outline has-[:focus-visible]:outline-3',
                                'has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-foreground',
                            ].join(' ')}
                        >
                            <input
                                type="radio"
                                name={name}
                                value={rating}
                                checked={value === rating}
                                aria-label={t(`feedback.rating.${rating}`)}
                                onChange={() => onChange(rating)}
                                /*
                                 * Tapping the chosen mark again clears it, as the handheld does. A radio
                                 * cannot be unchecked by clicking, so the click is caught before the browser
                                 * acts on it. Only a pointer can do this — a keyboard cannot uncheck a radio
                                 * either — but a guest who mistaps has no other way back to "not answered",
                                 * and the button below says "Skip" until something is rated.
                                 *
                                 * `value === rating` rather than `lit`: on a star scale the guest clears by
                                 * tapping the star they chose, not any star beneath it.
                                 */
                                onClick={(event) => {
                                    if (value !== rating) return;
                                    event.preventDefault();
                                    onChange(null);
                                }}
                                className="sr-only"
                            />

                            {stars ? <Star filled={lit} /> : <Face rating={rating} />}
                        </label>
                    );
                })}
            </div>
        </fieldset>
    );
}

/*
 * Five faces, told apart by the mouth alone. Eyes and outline are identical across all five so the difference
 * a guest reads is the one that means something.
 *
 * They used to sit over a row of stars - one star for a rating of one, five for a rating of five - which said
 * the same thing twice. That was dropped when brands gained the choice: a brand that wants stars now gets
 * stars, and mixing the two left neither reading cleanly.
 */
const MOUTHS: Record<Rating, string> = {
    1: 'M14 34 Q24 20 34 34',
    2: 'M16 31.5 Q24 26.5 32 31.5',
    3: 'M15 30 H33',
    4: 'M16 28.5 Q24 33.5 32 28.5',
    5: 'M14 26 Q24 40 34 26',
};

function Face({ rating }: { rating: Rating }) {
    return (
        <svg viewBox="0 0 48 48" className="size-9" fill="none" aria-hidden="true">
            <circle cx="24" cy="24" r="21" stroke="currentColor" strokeWidth="2.5" />
            <circle cx="17.5" cy="19" r="2.4" fill="currentColor" />
            <circle cx="30.5" cy="19" r="2.4" fill="currentColor" />
            <path d={MOUTHS[rating]} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
    );
}

/*
 * One star per point, filled up to the guest's rating. Decorative: the radio's own label already says
 * "3 of 5, neither", and announcing five more things per option would bury it.
 *
 * Bigger than the faces because each is one of five taps on a single row rather than a card to aim at, and a
 * star small enough to look elegant is small enough to mis-tap.
 */
function Star({ filled }: { filled: boolean }) {
    return (
        <svg viewBox="0 0 24 24" className="size-10" fill={filled ? 'currentColor' : 'none'} aria-hidden="true">
            <path
                d="M12 2.5 14.9 9l7.1.6-5.4 4.7 1.6 6.9L12 17.6 5.8 21.2l1.6-6.9L2 9.6 9.1 9Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
            />
        </svg>
    );
}
